import React from 'react';
import { motion } from 'framer-motion';
import { Lesson } from '@/features/courses/api/lesson-services';
import styles from './LessonContentRenderer.module.css';
import { useTimer } from '@/app/providers/TimerContext';

interface NextLessonCardProps {
    nextLesson: Lesson | null;
    currentLesson: Lesson;
    isCompleted?: boolean;
    onComplete: () => Promise<void>;
    onNavigate: (lesson: Lesson) => void;
}

const NextLessonCard: React.FC<NextLessonCardProps> = ({
    nextLesson,
    currentLesson,
    isCompleted = false,
    onComplete,
    onNavigate
}) => {
    const { lessonTime } = useTimer();
    const requiredSeconds = (currentLesson?.duration || 0) * 60;
    const isTimeRequirementMet = isCompleted || lessonTime >= requiredSeconds;
    const remainingSeconds = isCompleted ? 0 : Math.max(0, requiredSeconds - lessonTime);

    // Determine if we are at the end of a module
    const isEndOfModule = !nextLesson || (nextLesson.moduleId !== currentLesson.moduleId);

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
    };

    const handleCompleteAndNavigate = async () => {
        if (isCompleted) {
            if (nextLesson) {
                onNavigate(nextLesson);
            }
            return;
        }

        if (isTimeRequirementMet) {
            await onComplete();
        }
    };

    return (
        <motion.div
            className={styles.nextLessonCard}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.3 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
            <div className={styles.nextLessonHeader}>
                <span className={styles.nextLabel}>
                    {isEndOfModule ? 'End of Module' : 'Coming Up Next'}
                </span>
            </div>

            <div className={styles.nextLessonBody}>
                {isEndOfModule && !isCompleted ? (
                    <>
                        <h3 className={styles.nextTitle}>Module Completed!</h3>
                        <p className={styles.nextDescription}>
                            Excellent work! You've reached the end of this module. Finish your remaining time or proceed to the next chapter.
                        </p>
                    </>
                ) : nextLesson ? (
                    <>
                        <h3 className={styles.nextTitle}>{nextLesson.title}</h3>
                        {nextLesson.description && (
                            <p className={styles.nextDescription}>{nextLesson.description}</p>
                        )}
                    </>
                ) : (
                    <>
                        <h3 className={styles.nextTitle}>Course Completed!</h3>
                        <p className={styles.nextDescription}>
                            You've finished all lessons in the curriculum.
                        </p>
                    </>
                )}
            </div>

            <div className={styles.completionStatus}>
                {!isTimeRequirementMet ? (
                    <>
                        <div className={styles.instructionText}>
                            <p>You’re almost there! The State of Ohio requires a minimum time investment for this module to ensure all students fully digest the material. If you have time remaining, please take a moment to double-check the key Lesson Content above.</p>
                            <p>Repetition is vital for driving success. Please return to this card once you have reached the minimum duration to unlock the next phase of your training.</p>
                        </div>
                        <div className={styles.timeRequirement}>
                            Required time: {currentLesson.duration}m | Remaining: {formatTime(remainingSeconds)}
                        </div>
                    </>
                ) : (
                    <p className={styles.readyText}>
                        {isCompleted ? 'You have successfully completed this lesson.' : "You've met the time requirement and are ready to proceed!"}
                    </p>
                )}
            </div>

            <div className={styles.nextLessonFooter}>
                <button
                    className={`${styles.completeButton} ${!isTimeRequirementMet ? styles.disabled : ''} ${isTimeRequirementMet ? styles.pulse : ''}`}
                    onClick={handleCompleteAndNavigate}
                    disabled={!isTimeRequirementMet}
                >
                    {isCompleted
                        ? (nextLesson ? 'Proceed to Next Lesson' : 'Course Completed')
                        : (isTimeRequirementMet
                            ? (isEndOfModule ? 'Complete Module & Proceed' : 'Mark Complete & Proceed')
                            : `Wait ${formatTime(remainingSeconds)}`)}
                </button>

                {isTimeRequirementMet && nextLesson && (
                    <div className={styles.nextLessonIndicator}>
                        <div className={styles.indicatorArrow}>→</div>
                    </div>
                )}
            </div>
        </motion.div>
    );
};

export default NextLessonCard;
